/**
 * Upload an MP4 directly to S3 via presigned URL from getVideoUploadUrl mutation.
 * @param {Function} mutateFn - apollo mutate function (from useMutation)
 * @param {File} file
 * @returns {Promise<{ publicUrl: string, fileName: string }>}
 */

function formatVideoUploadError(error) {
  const gqlMsg = error?.graphQLErrors?.[0]?.message;
  const networkMsg =
    error?.networkError?.result?.errors?.[0]?.message ||
    error?.networkError?.message;
  const raw = gqlMsg || networkMsg || error?.message;

  if (raw && /transformation too large/i.test(raw)) {
    return "The video file is too large to upload through this form. Please try again or contact support.";
  }

  return raw || "Failed to start video upload";
}

export async function uploadVideoFileToS3(mutateFn, file) {
  if (!file) throw new Error("No file selected");

  const isMp4 =
    file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
  if (!isMp4) {
    throw new Error("Please upload a video in MP4 format only.");
  }

  let data;
  try {
    ({ data } = await mutateFn());
  } catch (error) {
    throw new Error(formatVideoUploadError(error));
  }

  const result = data?.getVideoUploadUrl;
  if (!result?.status || !result?.uploadUrl || !result?.publicUrl) {
    const msg =
      result?.errors?.[0]?.message ||
      result?.message ||
      "Failed to get upload URL";
    throw new Error(msg);
  }

  let putResponse;
  try {
    putResponse = await fetch(result.uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": "video/mp4",
      },
    });
  } catch (error) {
    throw new Error(
      error?.message ||
        "Network error while uploading video. Check your connection and try again."
    );
  }

  if (!putResponse.ok) {
    const status = putResponse.status;
    if (status === 403) {
      throw new Error(
        "Upload to storage was denied. Contact support if this persists."
      );
    }
    throw new Error(`Video upload failed (${status}). Please try again.`);
  }

  const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
  return {
    publicUrl: result.publicUrl,
    fileName: fileNameWithoutExtension,
  };
}
